/**
 * Find on this page: a magnifier at the top right opens a field under the header that narrows
 * what's on the page as you type. Closing it clears it; leaving the page forgets it.
 */
import { useState } from 'react'
import { IconSearch, IconX } from './Icons'
import { navigate } from '../lib/router'

export interface FindState {
  open: boolean
  q: string
  setQ: (q: string) => void
  toggle: () => void
  close: () => void
}

export function useFind(): FindState {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const close = () => {
    setOpen(false)
    setQ('')
  }
  return { open, q, setQ, close, toggle: () => (open ? close() : setOpen(true)) }
}

export function FindButton({ find }: { find: FindState }) {
  return (
    <button
      type="button"
      onClick={find.toggle}
      aria-label="Find on this page"
      aria-expanded={find.open}
      title="Find on this page"
      className={`flex h-8 w-8 flex-none items-center justify-center rounded-sm ${find.open ? 'bg-surface-3 text-fg' : 'text-muted hover:bg-surface-2 hover:text-fg'}`}
    >
      <IconSearch size={17} />
    </button>
  )
}

export function FindField({ find, placeholder }: { find: FindState; placeholder: string }) {
  if (!find.open) return null
  return (
    <div className="px-3 pb-2 md:px-5">
      <div className="relative mx-auto max-w-2xl">
        <IconSearch size={15} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-faint" />
        <input
          autoFocus
          type="search"
          enterKeyHint="search"
          value={find.q}
          onChange={(e) => find.setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              e.stopPropagation()
              find.close()
            }
          }}
          placeholder={placeholder}
          aria-label="Find on this page"
          className="h-10 w-full rounded-xl border border-line-2 bg-surface pr-10 pl-9 text-[1rem] outline-none focus:border-fg [&::-webkit-search-cancel-button]:hidden"
        />
        <button
          type="button"
          onClick={find.close}
          aria-label="Close find"
          className="absolute top-1/2 right-1 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm text-muted hover:bg-surface-2 hover:text-fg"
        >
          <IconX size={16} />
        </button>
      </div>
    </div>
  )
}

/** Shown when nothing on the page matches: a way through to the deep search. */
export function FindEmpty({ q, where }: { q: string; where: string }) {
  return (
    <div className="py-12 text-center">
      <p className="text-sm text-muted">
        Nothing {where} matches “{q.trim()}”.
      </p>
      <button
        type="button"
        onClick={() => navigate(`/search?q=${encodeURIComponent(q.trim())}`)}
        className="mt-3 inline-flex h-9 items-center gap-2 rounded-sm border border-line-2 px-4 text-sm hover:border-fg"
      >
        <IconSearch size={15} /> Search everything
      </button>
    </div>
  )
}
